import React, { useState, useEffect, useCallback } from 'react';
import {
  User,
  MapPin,
  Search,
  Plus,
  RefreshCw,
  Send,
  X,
  Edit2,
  Trash2,
  Download,
  MessageCircle,
  Users,
  FileText,
  Bell,
  CreditCard,
  Loader,
  CheckCircle2,
  SkipForward,
  RotateCcw,
  Image as ImageIcon,
} from 'lucide-react';
import { phonebookService } from '../../services/phonebookService';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const SOURCE_CONFIG = {
  all: { label: 'All Contacts', icon: Users, color: 'text-gray-600', bg: 'bg-gray-100' },
  user_account: { label: 'User Accounts', icon: User, color: 'text-blue-600', bg: 'bg-blue-100' },
  service_request: { label: 'Service Requests', icon: FileText, color: 'text-purple-600', bg: 'bg-purple-100' },
  service_reminder: { label: 'Service Reminders', icon: Bell, color: 'text-orange-600', bg: 'bg-orange-100' },
  invoice: { label: 'Invoices', icon: CreditCard, color: 'text-green-600', bg: 'bg-green-100' },
  manual: { label: 'Manual Entries', icon: Plus, color: 'text-teal-600', bg: 'bg-teal-100' },
};

const CustomerPhonebook = () => {
  const { isAdmin } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSource, setActiveSource] = useState('all');
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  // Modal states
  const [showManualModal, setShowManualModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
  });

  // Selection states for bulk WhatsApp
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Bulk WhatsApp states
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkMessage, setBulkMessage] = useState('');
  
  // Direct Image File Upload States
  const [bulkImageFile, setBulkImageFile] = useState(null);
  const [bulkImageUrl, setBulkImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  const [dispatchQueue, setDispatchQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDispatching, setIsDispatching] = useState(false);

  // Upload image to Cloudinary via backend
  const handleImageChange = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.target.files[0];
    if (!file) return;

    setBulkImageFile(file);
    setUploadingImage(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const response = await phonebookService.uploadImage(formDataUpload); 
      const uploadedUrl = response.data?.url || response.url;

      setBulkImageUrl(uploadedUrl);
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Image upload failed:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  // WhatsApp Media + Text Share Helper
  const shareWhatsAppWithMedia = async (phone, text, imageUrl) => {
    const cleanPhone = phone ? phone.replace(/\D/g, '') : '';
    const formattedPhone = cleanPhone.length === 10 ? `91${cleanPhone}` : cleanPhone;

    if (imageUrl && navigator.canShare) {
      try {
        const response = await fetch(imageUrl);
        if (!response.ok) throw new Error('Image fetch failed');
        
        const blob = await response.blob();
        const mimeType = blob.type || 'image/jpeg';
        const extension = mimeType.split('/')[1] || 'jpg';
        const file = new File([blob], `share_image.${extension}`, { type: mimeType });

        const shareData = {
          files: [file],
          text: text,
        };

        if (navigator.canShare(shareData)) {
          await navigator.share(shareData);
          return;
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.warn('Web Share failed, falling back to URL:', err);
      }
    }

    const encodedText = encodeURIComponent(text || '');
    const directUrl = formattedPhone
      ? `https://api.whatsapp.com/send?phone=${formattedPhone}&text=${encodedText}`
      : `https://api.whatsapp.com/send?text=${encodedText}`;

    window.open(directUrl, '_blank');
  };

  // Fetch phonebook data
  const fetchPhonebook = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 50,
        search: searchTerm || undefined,
        source: activeSource !== 'all' ? activeSource : undefined,
        isActive: 'true',
      };
      const response = await phonebookService.getPhonebook(params);
      setEntries(response.data);
      setPagination(response.pagination);
    } catch (error) {
      toast.error('Failed to load phonebook');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, activeSource]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await phonebookService.getStats();
      setStats(response.data);
    } catch (error) {
      // Silent fail for stats
    }
  }, []);

  useEffect(() => {
    fetchPhonebook(1);
    fetchStats();
  }, [fetchPhonebook, fetchStats]);

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPhonebook(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, fetchPhonebook]);

  // Sync phonebook
  const handleSync = async () => {
    if (!isAdmin) {
      toast.error('Only admins can sync phonebook');
      return;
    }
    try {
      setSyncing(true);
      await phonebookService.syncPhonebook();
      toast.success('Phonebook synced successfully!');
      fetchPhonebook(1);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to sync');
    } finally {
      setSyncing(false);
    }
  };

  // --- FIXED: Create manual entry ---
  const handleCreateManual = async (e) => {
    e.preventDefault();
    try {
      await phonebookService.createManualEntry({
        ...formData,
        source: 'manual',
      });
      toast.success('Contact added successfully!');
      setShowManualModal(false);
      setFormData({ name: '', phone: '', email: '', address: '', notes: '' });
      fetchPhonebook(1);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add contact');
    }
  };

  // Edit entry
  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editingEntry) return;
    try {
      await phonebookService.updateEntry(editingEntry._id, formData);
      toast.success('Contact updated successfully!');
      setShowEditModal(false);
      setEditingEntry(null);
      setFormData({ name: '', phone: '', email: '', address: '', notes: '' });
      fetchPhonebook(pagination.page);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update contact');
    }
  };

  // Delete entry
  const handleDelete = async (entry) => {
    if (!window.confirm(`Are you sure you want to delete ${entry.name}?`)) return;
    try {
      await phonebookService.deleteEntry(entry._id);
      toast.success('Contact deleted successfully!');
      fetchPhonebook(pagination.page);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete contact');
    }
  };

  // Open edit modal
  const openEditModal = (entry) => {
    if (entry.source !== 'manual') {
      toast.error('Only manual entries can be edited');
      return;
    }
    setEditingEntry(entry);
    setFormData({
      name: entry.name,
      phone: entry.phone,
      email: entry.email || '',
      address: entry.address || '',
      notes: entry.notes || '',
    });
    setShowEditModal(true);
  };

  // Download CSV
  const downloadCSV = () => {
    const headers = ['Name', 'Phone', 'Email', 'Address', 'Source', 'Notes'];
    const csvContent = [
      headers.join(','),
      ...entries.map((e) =>
        [
          `"${e.name}"`,
          e.phone,
          `"${e.email}"`,
          `"${e.address}"`,
          e.source,
          `"${e.notes}"`,
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'customer_phonebook.csv';
    link.click();
    toast.success('CSV downloaded!');
  };

  // ---- BULK DISPATCH LOGIC ----
  const handlePrepareQueue = async () => {
    if (!bulkMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    if (selectedIds.size === 0) {
      toast.error('No contacts selected. Please select contacts using checkboxes.');
      return;
    }

    try {
      setLoading(true);
      const allIds = Array.from(selectedIds);
      const result = await phonebookService.bulkWhatsApp(allIds, bulkMessage, bulkImageUrl);
      const links = result.data?.links || [];

      if (links.length === 0) {
        toast.error('No WhatsApp links could be generated');
        return;
      }

      setDispatchQueue(links);
      setCurrentIndex(0);
      setIsDispatching(true);
      toast.success(`Prepared queue for ${links.length} selected contacts`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to prepare bulk messages');
    } finally {
      setLoading(false);
    }
  };

  const handleDispatchCurrent = () => {
    if (currentIndex >= dispatchQueue.length) return;

    const currentItem = dispatchQueue[currentIndex];

    shareWhatsAppWithMedia(
      currentItem.phone,
      currentItem.message,
      bulkImageUrl || currentItem.imageUrl
    );

    if (currentIndex + 1 < dispatchQueue.length) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      setCurrentIndex(dispatchQueue.length);
      toast.success('🎉 All bulk messages dispatched successfully!');
    }
  };

  const handleSkipCurrent = () => {
    if (currentIndex + 1 < dispatchQueue.length) {
      setCurrentIndex((prev) => prev + 1);
      toast('Skipped contact');
    } else {
      setCurrentIndex(dispatchQueue.length);
    }
  };

  const handleResetQueue = () => {
    setDispatchQueue([]);
    setCurrentIndex(0);
    setIsDispatching(false);
    setBulkImageFile(null);
    setBulkImageUrl('');
  };

  const getSourceBadge = (source) => {
    const config = SOURCE_CONFIG[source] || SOURCE_CONFIG.manual;
    return (
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.color}`}
      >
        <config.icon size={12} />
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Customer Phonebook</h1>
          <p className="text-sm text-gray-500 mt-1">
            {pagination.total} contacts found
            {stats && (
              <span className="ml-2">
                |{' '}
                {stats.bySource
                  .map((s) => `${SOURCE_CONFIG[s._id]?.label || s._id}: ${s.count}`)
                  .join(' | ')}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <button
              onClick={handleSync}
              disabled={syncing}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50 text-xs font-semibold"
            >
              <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
              {syncing ? 'Syncing...' : 'Sync Data'}
            </button>
          )}

          {/* FIXED: Open Manual Contact Modal */}
          <button
            onClick={() => {
              setFormData({ name: '', phone: '', email: '', address: '', notes: '' });
              setShowManualModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors text-xs font-semibold"
          >
            <Plus size={14} />
            Add Manual
          </button>

          <button
            onClick={() => {
              handleResetQueue();
              setShowBulkModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors text-xs font-semibold"
          >
            <MessageCircle size={14} />
            WhatsApp Bulk ({selectedIds.size})
          </button>

          <button
            onClick={downloadCSV}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors text-xs font-semibold"
          >
            <Download size={14} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Source Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(SOURCE_CONFIG).map(([key, config]) => (
          <button
            key={key}
            onClick={() => setActiveSource(key)}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-medium transition-all ${
              activeSource === key
                ? 'bg-indigo-600 text-white shadow-md'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
            }`}
          >
            <config.icon size={14} />
            {config.label}
          </button>
        ))}
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          size={18}
        />
        <input
          type="text"
          placeholder="Search by name, phone, or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none bg-white text-xs"
        />
      </div>

      {/* Contacts Table */}
      {loading ? (
        <div className="text-center py-12">
          <Loader className="animate-spin mx-auto mb-3 text-indigo-600" size={28} />
          <p className="text-xs text-gray-500">Loading contacts...</p>
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
          <Users className="mx-auto mb-4 text-gray-300" size={40} />
          <p className="text-base font-bold text-gray-700 mb-1">No contacts found</p>
          <p className="text-xs text-gray-400">
            {activeSource === 'all'
              ? 'Sync data from sources or add a manual entry to get started.'
              : `No contacts from ${SOURCE_CONFIG[activeSource]?.label}. Try a different source.`}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="p-3 text-left w-10">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectAll(checked);
                        if (checked) {
                          setSelectedIds(new Set(entries.map((e) => e._id)));
                        } else {
                          setSelectedIds(new Set());
                        }
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                    />
                  </th>
                  <th className="p-3 text-left font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="p-3 text-left font-semibold text-gray-500 uppercase tracking-wider">Phone</th>
                  <th className="p-3 text-left font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Email</th>
                  <th className="p-3 text-left font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Source</th>
                  <th className="p-3 text-right font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {entries.map((entry) => (
                  <tr key={entry._id} className="hover:bg-gray-50/80 transition-colors">
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(entry._id)}
                        onChange={(e) => {
                          const newSet = new Set(selectedIds);
                          if (e.target.checked) {
                            newSet.add(entry._id);
                          } else {
                            newSet.delete(entry._id);
                          }
                          setSelectedIds(newSet);
                          setSelectAll(newSet.size === entries.length);
                        }}
                        className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                      />
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs shrink-0">
                          {entry.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-800 truncate">{entry.name}</p>
                          {entry.address && (
                            <p className="text-[10px] text-gray-400 flex items-center gap-0.5 truncate">
                              <MapPin size={9} className="shrink-0" />
                              {entry.address}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-3 font-mono font-medium text-gray-700">{entry.phone}</td>
                    <td className="p-3 hidden md:table-cell text-gray-500 truncate max-w-[180px]">{entry.email || '—'}</td>
                    <td className="p-3 hidden lg:table-cell">
                      {getSourceBadge(entry.source)}
                      {entry.notes && (
                        <p className="text-[10px] text-gray-400 mt-0.5 truncate max-w-[150px]">{entry.notes}</p>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => shareWhatsAppWithMedia(entry.phone, '', entry.imageUrl)}
                          className="p-1.5 hover:bg-green-50 rounded-lg text-green-600 transition-colors"
                          title="Open WhatsApp Chat"
                        >
                          <MessageCircle size={15} />
                        </button>
                        {entry.source === 'manual' && (
                          <button
                            onClick={() => openEditModal(entry)}
                            className="p-1.5 hover:bg-blue-50 rounded-lg text-blue-600 transition-colors"
                            title="Edit"
                          >
                            <Edit2 size={15} />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(entry)}
                          className="p-1.5 hover:bg-red-50 rounded-lg text-red-500 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
              <p className="text-xs text-gray-500">
                Page {pagination.page} of {pagination.pages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => fetchPhonebook(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="px-3 py-1.5 text-xs bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 font-medium"
                >
                  Previous
                </button>
                <button
                  onClick={() => fetchPhonebook(pagination.page + 1)}
                  disabled={pagination.page >= pagination.pages}
                  className="px-3 py-1.5 text-xs bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 font-medium"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Manual Add Modal */}
      {showManualModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-800">Add Manual Contact</h2>
              <button
                onClick={() => setShowManualModal(false)}
                className="p-1 hover:bg-gray-100 rounded-lg text-gray-400"
              >
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleCreateManual} className="p-5 space-y-3.5 text-xs">
              <div>
                <label className="block font-semibold text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block font-semibold text-gray-700 mb-1">Phone *</label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="+91 9876543210"
                />
              </div>
              <div>
                <label className="block font-semibold text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <label className="block font-semibold text-gray-700 mb-1">Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={2}
                  placeholder="Full address"
                />
              </div>
              <div>
                <label className="block font-semibold text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={2}
                  placeholder="Additional notes"
                />
              </div>
              <div className="flex gap-2.5 pt-2">
                <button
                  type="button"
                  onClick={() => setShowManualModal(false)}
                  className="flex-1 py-2.5 border border-gray-200 rounded-xl font-medium text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2.5 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700"
                >
                  Add Contact
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingEntry && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-800">Edit Contact</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingEntry(null);
                }}
                className="p-1 hover:bg-gray-100 rounded-lg text-gray-400"
              >
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleEdit} className="p-5 space-y-3.5 text-xs">
              <div>
                <label className="block font-semibold text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block font-semibold text-gray-700 mb-1">Phone *</label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block font-semibold text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block font-semibold text-gray-700 mb-1">Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={2}
                />
              </div>
              <div>
                <label className="block font-semibold text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={2}
                />
              </div>
              <div className="flex gap-2.5 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingEntry(null);
                  }}
                  className="flex-1 py-2.5 border border-gray-200 rounded-xl font-medium text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2.5 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700"
                >
                  Update Contact
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk WhatsApp Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h2 className="text-base font-bold text-gray-800">Bulk WhatsApp</h2>
                <p className="text-xs text-gray-500">
                  Send personalized messages to selected contacts
                </p>
              </div>
              <button
                onClick={() => {
                  setShowBulkModal(false);
                  handleResetQueue();
                }}
                className="p-1 hover:bg-gray-100 rounded-lg text-gray-400"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {!isDispatching ? (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Message Template ({selectedIds.size} contacts selected)
                    </label>
                    <p className="text-[11px] text-gray-400 mb-2">
                      Use <code className="bg-gray-100 px-1 py-0.5 rounded text-gray-700">{'{name}'}</code> as a placeholder for the customer's name.
                    </p>
                    <textarea
                      required
                      value={bulkMessage}
                      onChange={(e) => setBulkMessage(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-xs"
                      rows={4}
                      placeholder={`Hi {name},\n\nCheck out our latest product offers!\n\nThank you,\nTeam ProNest`}
                    />
                  </div>

                  {/* Direct Image File Picker */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
                      <ImageIcon size={14} /> Attach Image (Optional)
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="w-full text-xs text-gray-500 file:mr-3 file:py-2 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100 cursor-pointer"
                      />
                      {uploadingImage && <Loader size={16} className="animate-spin text-green-600" />}
                    </div>

                    {bulkImageUrl && (
                      <div className="mt-2 flex items-center gap-2 bg-green-50 p-2 rounded-lg border border-green-100 text-[11px] text-green-700">
                        <img src={bulkImageUrl} alt="Preview" className="w-8 h-8 object-cover rounded-md" />
                        <span className="truncate">Image uploaded and ready for batch share!</span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowBulkModal(false);
                        handleResetQueue();
                      }}
                      className="flex-1 py-2.5 border border-gray-200 rounded-xl text-xs font-semibold text-gray-600 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handlePrepareQueue}
                      disabled={!bulkMessage.trim() || selectedIds.size === 0 || uploadingImage}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 text-xs font-semibold disabled:opacity-40"
                    >
                      <Send size={14} />
                      Prepare Queue ({selectedIds.size})
                    </button>
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="bg-gray-50 border border-gray-100 p-4 rounded-xl space-y-2">
                    <div className="flex items-center justify-between text-xs font-bold text-gray-700">
                      <span>Queue Status</span>
                      <span className="text-green-600">
                        {currentIndex} / {dispatchQueue.length} Dispatched
                      </span>
                    </div>

                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-green-600 h-full transition-all duration-300"
                        style={{
                          width: `${(currentIndex / dispatchQueue.length) * 100}%`,
                        }}
                      />
                    </div>
                  </div>

                  {currentIndex < dispatchQueue.length ? (
                    <div className="border border-green-200 bg-green-50/40 p-4 rounded-xl space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-[10px] font-bold uppercase tracking-wider text-green-700">Active Recipient</span>
                          <h4 className="text-sm font-bold text-gray-800">
                            {dispatchQueue[currentIndex].name}
                          </h4>
                          <p className="text-xs font-mono text-gray-500">
                            {dispatchQueue[currentIndex].phone}
                          </p>
                        </div>
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold text-xs">
                          #{currentIndex + 1}
                        </div>
                      </div>

                      <div className="bg-white p-2.5 rounded-lg border border-green-100 text-xs text-gray-600 italic">
                        "{dispatchQueue[currentIndex].message || 'Personalized WhatsApp Message'}"
                      </div>

                      <div className="flex gap-2 pt-1">
                        <button
                          onClick={handleSkipCurrent}
                          className="px-3 py-2 border border-gray-200 bg-white rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 flex items-center gap-1"
                        >
                          <SkipForward size={12} />
                          Skip
                        </button>
                        <button
                          onClick={handleDispatchCurrent}
                          className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 shadow-sm active:scale-95 transition-all"
                        >
                          <Send size={14} />
                          <span>Send Message & Next</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-green-50 rounded-xl border border-green-200 space-y-2">
                      <CheckCircle2 size={36} className="text-green-600 mx-auto" />
                      <h4 className="text-sm font-bold text-gray-800">Campaign Completed!</h4>
                      <p className="text-xs text-gray-500">All {dispatchQueue.length} messages have been dispatched.</p>
                      <button
                        onClick={handleResetQueue}
                        className="mt-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 mx-auto"
                      >
                        <RotateCcw size={12} />
                        Reset Queue
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerPhonebook;